import React, { useLayoutEffect } from 'react'
import { Helmet } from 'react-helmet'

import { browserHistory } from '../../services/history'
import { homeTitle } from '../../lib/home-title'

import { HeroFold } from './sections/hero'
import { heroReg, HeroRegLoader } from './sections/hero/reg/loader'
import { LocationBlurb, readArea } from './sections/location-blurb'
import { HowItWorks } from './sections/how-it-works'
import { WhatsIncluded } from './sections/whats-included'
import { RecentWork } from './sections/recent-work'
import { Reviews } from './sections/reviews'
import { WhoTurnsUp } from './sections/who-turns-up'
import { AreasAndCta } from './sections/areas-cta'

import '../../assets/styles/home.css'

// The home page is a running order and nothing else. Every section lives in
// its own file under ./sections and owns its own markup, copy and styles;
// this file owns which sections exist, in what order, and nothing more. One
// section is one slot and one slot is one job.
//
// Order, backgrounds and the objection each section answers are set by
// gowashmycar-research/homepage-design-direction-2026-10-04.md §7. The
// alternating paper / stone band is part of the design: do not change a
// section's background without changing its neighbours.
//
// Sections render nothing while they are unbuilt. That is deliberate - an
// empty slot is visible in the running order, a section quietly missing from
// a 400-line component is not.
//
// Read client/gwmc/DESIGN.md before touching any of them.
//
//   slot            file                                 band    direction
//   ------------------------------------------------------------------------
//   HeroFold        sections/hero/                       paper   §2, §7.1
//   LocationBlurb   sections/location-blurb/             stone   §7 (area pages only)
//   HowItWorks      sections/how-it-works/               stone   §7.2
//   WhatsIncluded   sections/whats-included/             paper   §7.3
//   RecentWork      sections/recent-work/                stone   §7.4
//   Reviews         sections/reviews/                    paper   §7.5
//   WhoTurnsUp      sections/who-turns-up/               stone   §7.6
//   AreasAndCta     sections/areas-cta/                  paper   §7.7
//
// The header (§2.1.1, §2.2.1) and the footer (§7.8) are page chrome, not home
// page sections: components/header/ and components/footer/. The booking
// screen is pages/booking/ and docs/ONE_SCREEN_CHECKOUT_SPEC.md governs it.
//
// AREA PAGES RUN A SHORTER ORDER. The 52 area routes render this component
// too, and with the home page's full running order the local section sat in
// a ~12,200px phone page among five generic sections. Someone who searched
// "car wash Battersea" needs the fold, the proof that we work there, the
// reviews, how a booking goes and the closing CTA, so an area page is:
//
//   HeroFold (paper), LocationBlurb (stone), Reviews (paper),
//   HowItWorks (stone), AreasAndCta (paper)
//
// WhatsIncluded, RecentWork and WhoTurnsUp are home page only: the fold
// already prices the three washes and introduces Barry, and the area's own
// recent washes stand in for the London-wide gallery. `/` is unchanged.
export const HomePage = React.memo(() => {
  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const areaPage = Boolean(readArea())

  return (
    <div className="home">
      {/* The title, for a client-side route change only: the server has
          already rendered the same sentence into <title>, and on an area page
          the guard below leaves the server's own title alone. The "from" figure
          is derived from the published catalogue, never typed: lib/home-title.js
          and DESIGN.md §8.10. */}
      {browserHistory.location.pathname === '/' && <Helmet title={homeTitle()} />}

      {/* LANDING_HERO reg-price: challenger B of the fold, home page only
          (sections/hero/reg). Otherwise, and if B cannot load, the control. */}
      {heroReg() && !areaPage ? <HeroRegLoader fallback={<HeroFold />} /> : <HeroFold />}
      <LocationBlurb />
      {areaPage ? (
        <>
          <Reviews />
          <HowItWorks />
        </>
      ) : (
        <>
          <HowItWorks />
          <WhatsIncluded />
          <RecentWork />
          <Reviews />
          <WhoTurnsUp />
        </>
      )}
      <AreasAndCta />
    </div>
  )
})
